import React from "react";
import getRowFormettedLogoData from "../../../../../utils/getRowFormettedLogoData";
import { LOGOS_PER_ROW } from "../../../../Options/OptionBlocks/Logos/logoLayoutUtils";
import { getLogoAssetUrl } from "./logoAssetUrl";

const hasAsset = (logo) => Boolean(logo?.thumbnail || logo?.url);

const selectLogoInEditor = (blockIndex, logo) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("scrollx:logo-select", {
      detail: {
        blockIndex,
        row: logo?.row || 0,
        position: logo?.position || 0,
      },
    }),
  );
};

const LogoRow = ({ row, rowIndex, blockIndex, options, managedLayout }) => {
  const visibleLogos = managedLayout ? row.filter(hasAsset) : row;
  if (!visibleLogos.length) return null;

  const horizontalGap = Math.max(0, Number(options?.gap) || 0);
  const slotPercentage = 100 / LOGOS_PER_ROW;
  const gapOffset =
    (horizontalGap * (LOGOS_PER_ROW - 1)) / LOGOS_PER_ROW;
  const managedSlotWidth = horizontalGap
    ? `calc(${slotPercentage}% - ${gapOffset}px)`
    : `${slotPercentage}%`;

  const emptyLogoStyle = {
    height: `${options?.column_width}%`,
    width: `${options?.column_width}%`,
    borderRadius: "10px",
  };

  return (
    <div
      className="h-full w-full flex flex-row justify-evenly items-center"
      id="logo_container_logo"
      data-testid="logo-row"
      data-logo-row={rowIndex}
      style={managedLayout ? { gap: `${horizontalGap}px` } : undefined}
    >
      {visibleLogos.map((logo, logoIndex) => (
        <div
          key={`${logo?.row || rowIndex}-${logo?.position || logoIndex}-${logo?.url || logoIndex}`}
          id={`${blockIndex}_logo_${rowIndex}_${logoIndex}`}
          data-testid="logo-slot"
          data-logo-position={logo?.position || 0}
          style={{
            height: "auto",
            width: managedLayout ? managedSlotWidth : "100%",
            flex: managedLayout ? `0 0 ${managedSlotWidth}` : undefined,
            minWidth: 0,
            position: "relative",
            transform: `scale(${(logo?.scale ?? 80) / 100})`,
            top: logo?.offset_y || 0,
            left: logo?.offset_x || 0,
            borderRadius: "10px",
            cursor: hasAsset(logo) ? "pointer" : "default",
          }}
          onClick={() => {
            if (hasAsset(logo)) selectLogoInEditor(blockIndex, logo);
          }}
        >
          {hasAsset(logo) ? (
            <img
              crossOrigin="anonymous"
              src={getLogoAssetUrl(logo)}
              style={{
                height: "100%",
                width: "100%",
                objectFit: managedLayout ? "contain" : undefined,
              }}
              alt={logo.title || "logo"}
            />
          ) : (
            <div
              className="border-for-empty-logo"
              data-testid="empty-logo-slot"
              style={emptyLogoStyle}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default function Logo({ options, items, idx }) {
  const { logoItems } = getRowFormettedLogoData(items || []);
  const managedLayout = ["row", "grid"].includes(options?.logo_layout);

  return (
    <div
      className="logo_container mx-auto"
      data-testid="logo-container"
      data-logo-layout={options?.logo_layout || "legacy"}
      style={{
        width: `${options?.column_width || 100}%`,
        ...(managedLayout
          ? {
              height: "100%",
              display: "grid",
              gridTemplateRows: `repeat(${Math.max(logoItems.length, 1)}, minmax(0, 1fr))`,
              rowGap: `${options?.gapVertical || 0}px`,
            }
          : {}),
      }}
    >
      {logoItems.map((row, rowIndex) => (
        <LogoRow
          key={rowIndex}
          row={row || []}
          rowIndex={rowIndex}
          blockIndex={idx}
          options={options}
          managedLayout={managedLayout}
        />
      ))}
    </div>
  );
}
